import { DialogCloseEvent, DialogComponent } from "@theme/dialog";

class ContactDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener("click", this.#handleContactLink);
    window.addEventListener("hashchange", this.#handleHashChange);
    this.addEventListener("keydown", this.#handleTabKey);
    this.addEventListener("submit", this.#handleContactSubmit);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    if (window.Shopify?.designMode) {
      this.addEventListener("shopify:block:select", this.#handleBlockSelect);
    }

    const url = new URL(window.location.href);
    const contactWasPosted = url.searchParams.get("contact_posted") === "true";

    const requestedTab = this.#tabFromHash(window.location.hash);

    if (requestedTab || contactWasPosted) {
      requestAnimationFrame(() => {
        this.openTab(contactWasPosted ? "contact" : requestedTab);
        if (contactWasPosted) this.#showSubmittedState();
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.#handleContactLink);
    window.removeEventListener("hashchange", this.#handleHashChange);
    this.removeEventListener("keydown", this.#handleTabKey);
    this.removeEventListener("submit", this.#handleContactSubmit);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener("shopify:block:select", this.#handleBlockSelect);

    super.disconnectedCallback();
  }

  open = (event) => {
    event?.preventDefault?.();
    this.openTab("contact");
  };

  openTab = (tabName = "contact") => {
    this.selectTab(tabName);
    this.showDialog();
  };

  selectTab = (eventOrTab) => {
    const tabTrigger =
      typeof eventOrTab === "string"
        ? null
        : eventOrTab?.target?.closest?.("[data-contact-tab]") ??
          eventOrTab?.currentTarget?.closest?.("[data-contact-tab]");
    const tabName = typeof eventOrTab === "string" ? eventOrTab : tabTrigger?.dataset.contactTab;

    if (!tabName) return;

    const tabs = Array.from(this.querySelectorAll("[data-contact-tab]"));
    const panels = Array.from(this.querySelectorAll("[data-contact-panel]"));

    tabs.forEach((tab) => {
      const isActive = tab.dataset.contactTab === tabName;
      tab.setAttribute("aria-selected", String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.contactPanel !== tabName;
    });

    this.querySelector("dialog")?.setAttribute("aria-labelledby", `ContactDrawerTab-${tabName}`);

    const summary = this.querySelector("[data-contact-summary]");
    if (summary) summary.hidden = tabName !== "contact";
  };

  #handleContactLink = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const link = event.target.closest?.("a[href]");
    if (!link) return;

    const url = new URL(link.href, window.location.href);
    const tabName = this.#tabFromHash(url.hash);
    if (!tabName) return;

    const isCurrentPage =
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search;

    if (!isCurrentPage) return;

    event.preventDefault();
    this.openTab(tabName);
  };

  #handleHashChange = () => {
    const tabName = this.#tabFromHash(window.location.hash);
    if (tabName) this.openTab(tabName);
  };

  #handleDialogClose = () => {
    this.#resetContactForms();

    const url = new URL(window.location.href);
    const hasContactState = Boolean(this.#tabFromHash(url.hash)) || url.searchParams.has("contact_posted");
    if (!hasContactState) return;

    url.hash = "";
    url.searchParams.delete("contact_posted");
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
  };

  #tabFromHash(hash) {
    const normalizedHash = hash.toLowerCase();

    if (normalizedHash === "#contact") return "contact";
    if (normalizedHash === "#book") return "booking";

    return null;
  }

  #handleTabKey = (event) => {
    const currentTab = event.target.closest?.("[data-contact-tab]");
    if (!currentTab || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    const tabs = Array.from(this.querySelectorAll("[data-contact-tab]"));
    const currentIndex = tabs.indexOf(currentTab);
    if (currentIndex === -1) return;

    event.preventDefault();

    let nextIndex = currentIndex;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;

    const nextTab = tabs[nextIndex];
    this.selectTab(nextTab.dataset.contactTab);
    nextTab.focus();
  };

  #handleBlockSelect = (event) => {
    const blockId = event.detail?.blockId;
    const selectedBlock = blockId
      ? this.querySelector(`[data-shopify-editor-block="${CSS.escape(blockId)}"]`)
      : event.target;
    const panel = selectedBlock?.closest?.("[data-contact-panel]");
    const summary = selectedBlock?.closest?.("[data-contact-summary]");

    if (!panel && !summary) return;

    this.selectTab(summary ? "contact" : panel.dataset.contactPanel);
    this.showDialog();
  };

  #handleContactSubmit = (event) => {
    const form = event.target.closest?.("[data-contact-summary] form");
    if (!form || !form.matches('form[action*="/contact"]')) return;

    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;

    this.#rememberButtonLabel(button);
    this.#setButtonState(button, this.dataset.formSubmittingLabel || "Sender…", "submitting");
    this.#setContactReturnUrl(form);
  };

  #setContactReturnUrl(form) {
    let returnTo = form.querySelector('input[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement("input");
      returnTo.type = "hidden";
      returnTo.name = "return_to";
      form.append(returnTo);
    }

    const url = new URL(window.location.href);
    url.searchParams.set("contact_posted", "true");
    url.hash = "#contact";
    returnTo.value = `${url.pathname}${url.search}${url.hash}`;
  }

  #showSubmittedState() {
    const form = this.querySelector("[data-contact-summary] form");
    if (!form) return;

    form.hidden = true;

    const confirmation = document.createElement("div");
    confirmation.className = "contact-drawer__confirmation h4";
    confirmation.dataset.contactConfirmation = "";
    confirmation.setAttribute("role", "status");
    confirmation.setAttribute("tabindex", "-1");
    confirmation.textContent = this.dataset.formSuccessLabel || "Formular modtaget";
    form.after(confirmation);

    requestAnimationFrame(() => confirmation.focus());
  }

  #rememberButtonLabel(button) {
    if (button.dataset.originalLabel) return;

    button.dataset.originalLabel = button.querySelector(".btn__text")?.textContent?.trim() || button.textContent.trim();
  }

  #setButtonState(button, label, state) {
    button.disabled = true;
    button.setAttribute("aria-busy", state === "submitting" ? "true" : "false");
    button.classList.toggle("is-success", state === "success");

    button.querySelectorAll(".btn__text, .btn__icon-text").forEach((element) => {
      element.textContent = label;
    });
  }

  #restoreButton(button) {
    const label = button.dataset.originalLabel;
    if (label) {
      button.querySelectorAll(".btn__text, .btn__icon-text").forEach((element) => {
        element.textContent = label;
      });
    }

    button.disabled = false;
    button.removeAttribute("aria-busy");
    button.classList.remove("is-success");
  }

  #resetContactForms() {
    this.querySelector("[data-contact-confirmation]")?.remove();

    this.querySelectorAll("[data-contact-summary] form").forEach((form) => {
      form.hidden = false;
      const button = form.querySelector('button[type="submit"]');
      if (button) this.#restoreButton(button);
    });
  }
}

if (!customElements.get("contact-drawer-component")) {
  customElements.define("contact-drawer-component", ContactDrawerComponent);
}
